import {
  HomeMinor,
  TemplateMinor,
  StoreMinor,
  ProductsMinor,
  CustomersMinor,
  RefreshMinor,
  AnalyticsMinor,
  ConnectMinor,
  InsertDynamicSourceMinor,
  ChevronLeftMinor,
  ChevronRightMinor,
} from '@shopify/polaris-icons';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Navigation } from '@shopify/polaris';
import { filterPermissionsMenu } from '../../../services/permissionsService';
import { useGlobalStatus } from '../../../context/GlobalStatusContext';

/**
 * Nombre del elemento bajo el ratón o el foco cuando el menú está plegado.
 * Va en un portal al `body`: el contenedor del menú de Polaris tiene
 * `transform` y `overflow`, y un `position: fixed` dentro quedaría recortado.
 */
const useCollapsedTip = (collapsed) => {
  const [tip, setTip] = useState(null);
  const show = (e) => {
    // En móvil el menú es el cajón de Polaris, siempre desplegado (mismo corte que App.css).
    if (!collapsed || !window.matchMedia('(min-width: 48em)').matches) return;
    const item = e.target.closest('.Polaris-Navigation__Item, .mx-nav-toggle');
    if (!item) {
      setTip(null);
      return;
    }
    const text = item.querySelector('.Polaris-Navigation__Text')?.textContent || item.getAttribute('aria-label');
    // El Badge de Polaris lleva un «Nuevo» oculto para lectores: solo la cifra.
    const badge = item.querySelector('.Polaris-Navigation__Badge')?.textContent.replace(/\D+/g, '');
    const r = item.getBoundingClientRect();
    setTip({ text: badge ? `${text} (${badge})` : text, left: r.right + 8, top: r.top + r.height / 2 });
  };
  const hide = () => setTip(null);
  const node =
    collapsed && tip
      ? createPortal(
          <div role="tooltip" className="mx-nav-tip" style={{ left: tip.left, top: tip.top }}>
            {tip.text}
          </div>,
          document.body,
        )
      : null;
  // `mouseleave` no es fiable en un elemento sin caja (`display: contents`):
  // se oculta cuando el ratón sale hacia algo de fuera del menú.
  const out = (e) => {
    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) hide();
  };
  return { handlers: { onMouseOver: show, onFocus: show, onMouseOut: out, onBlur: hide }, node };
};

export const MainNavigation = ({ collapsed = false, onToggleCollapsed }) => {
  const { status } = useGlobalStatus();
  const location = useLocation();
  const tip = useCollapsedTip(collapsed);

  const userMenuItems = [
    {
      label: 'Inicio',
      url: '/dashboard',
      icon: HomeMinor,
      permissions: true,
    },
    {
      label: 'Recepción España',
      url: '/deliveries',
      disabled: false,
      permissions: ['DELIVERY:READ'],
      icon: ConnectMinor,
      badge: status && status.deliveries.count > 0 ? status.deliveries.count.toString() : undefined,
    },

    {
      label: 'Transpasos',
      permissions: ['STOCK:READ'],
      url: '/transfers',
      icon: RefreshMinor,
      badge: status && status.transfers.count > 0 ? status.transfers.count.toString() : undefined,
    },
    {
      label: 'Inventarios',
      permissions: ['DEPOSIT:READ'],
      url: '/inventories',
      icon: InsertDynamicSourceMinor,
      badge: status && status.inventories.count > 0 ? status.inventories.count.toString() : undefined,
    },
    {
      label: 'Ventas',
      url: '/sells',
      icon: StoreMinor,
      subNavigationItems: [
        {
          url: '/sells',
          disabled: false,
          label: 'WholeSale',
          permissions: ['SELL:READ'],
        },
        {
          url: '/sells/file',
          disabled: false,
          label: 'Ficheros EPH',
          permissions: ['SELL:CREATE'],
        },
        {
          url: '/customers',
          disabled: false,
          label: 'Clientes',
          permissions: ['CUSTOMER:READ'],
        },
      ],
    },
    {
      label: 'Depósitos',
      url: '/deposits',
      icon: TemplateMinor,
      permissions: ['DEPOSIT:READ'],
    },
    {
      label: 'Productos',
      url: '/products',
      icon: ProductsMinor,
      permissions: ['PRODUCT:READ'],
    },
    {
      url: '/leads',
      disabled: false,
      label: 'Contactos',
      icon: CustomersMinor,
      permissions: ['LEAD:READ'],
    },
  ];

  const reportsMenuItems = [
    {
      label: 'Ventas EPH',
      url: '/reports/sells/eph',
      permissions: ['SELL:REPORTS'],
      icon: AnalyticsMinor,
    },
    {
      label: 'Ventas Outlet',
      url: '/reports/sells/outlet',
      permissions: ['SELL:REPORTS'],
      icon: AnalyticsMinor,
    },
    {
      label: 'Facturación Estimada',
      url: '/reports/invoiced',
      permissions: ['SELL:REPORTS'],
      icon: AnalyticsMinor,
    },
    {
      label: 'Valoración Existencias',
      url: '/reports/stocks',
      permissions: ['DEPOSIT:REPORTS'],
      icon: AnalyticsMinor,
    },
    {
      label: 'Eficacia Comercial',
      url: '/reports/efficacy',
      permissions: ['SELL:REPORTS'],
      icon: AnalyticsMinor,
    },
    // Reposición: oculto temporalmente hasta su lanzamiento oficial.
    // La ruta /reports/replenishment sigue accesible directamente,
    // sólo se retira la opción del menú.
    // {
    //   label: 'Reposición',
    //   url: '/reports/replenishment',
    //   permissions: ['SELL:REPORTS'],
    //   icon: AnalyticsMinor,
    // },
  ];

  const adminMenuItems = [
    {
      label: 'Usuarios',
      url: '/users',
      icon: CustomersMinor,
      subNavigationItems: [
        {
          label: 'Usuarios',
          url: '/users',
          disabled: false,
          permissions: ['USER:READ'],
        },
        {
          url: '/users/roles',
          disabled: false,
          label: 'Roles',
          permissions: ['ROLE:READ'],
        },
      ],
    },
  ];

  let filterUserMenuItems = filterPermissionsMenu(userMenuItems);

  // // Inject badges from global status
  // if (status) {
  //   filterUserMenuItems = filterUserMenuItems.map((item) => {
  //     if (item.label === 'Recepción España' && status.deliveries?.count > 0) {
  //       return { ...item, badge: status.deliveries.count.toString() };
  //     }
  //     if (item.label === 'Transpaso entre depósitos' && status.transfers?.count > 0) {
  //       return { ...item, badge: status.transfers.count.toString() };
  //     }
  //     if (item.label === 'Inventarios' && status.inventories?.count > 0) {
  //       return { ...item, badge: status.inventories.count.toString() };
  //     }
  //     return item;
  //   });
  // }

  const filterAdminMenuItems = filterPermissionsMenu(adminMenuItems);
  const filterReportsMenuItems = filterPermissionsMenu(reportsMenuItems);

  // `display: contents`: el envoltorio solo escucha (ratón y foco para el
  // nombre en plegado); no entra en la maqueta del Frame.
  return (
    <div style={{ display: 'contents' }} {...tip.handlers}>
      <Navigation location={location.pathname}>
        <>
          {filterUserMenuItems.length > 0 && <Navigation.Section items={filterUserMenuItems} />}
          {filterReportsMenuItems.length > 0 && <Navigation.Section title="Informes" items={filterReportsMenuItems} />}
          {filterAdminMenuItems.length > 0 && (
            <Navigation.Section title="Administración" items={filterAdminMenuItems} />
          )}
          {onToggleCollapsed && (
            <button
              type="button"
              className="mx-nav-toggle"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? 'Desplegar menú' : 'Plegar menú'}
              aria-expanded={!collapsed}
            >
              {collapsed ? <ChevronRightMinor aria-hidden /> : <ChevronLeftMinor aria-hidden />}
              <span className="mx-nav-toggle__text">Plegar menú</span>
            </button>
          )}
        </>
      </Navigation>
      {tip.node}
    </div>
  );
};
